import { useState, useCallback, useEffect } from 'react'
import { apiFetch } from '@/lib/api'
import { useToast } from '@/components/ToastContainer'
import { Modal, Button, Select, Input } from '@/ui'
import styles from './EditModelModal.module.css'

interface BaseModel {
  id: string
  name: string
  description: string
  modelType: 'image' | 'text'
}

interface ImageModel extends BaseModel {
  modelType: 'image'
  costPerImage: number
  facePreservation: string
  speed: string
  supportsReferenceImage: boolean
  aspectRatio: string | null
  imageSize: string | null
}

interface TextModel extends BaseModel {
  modelType: 'text'
  costPer1kInputTokens: number
  costPer1kOutputTokens: number
  maxContextTokens: number
}

type EditableModel = ImageModel | TextModel

interface EditModelModalProps {
  open: boolean
  model: EditableModel | null
  onClose: () => void
  onSaved: () => void
}

const FACE_OPTIONS = [
  { value: 'excellent', label: 'excellent' },
  { value: 'good', label: 'good' },
  { value: 'fair', label: 'fair' },
  { value: 'poor', label: 'poor' },
]

const SPEED_OPTIONS = [
  { value: 'fast', label: 'fast' },
  { value: 'medium', label: 'medium' },
  { value: 'slow', label: 'slow' },
]

const ASPECT_RATIO_OPTIONS = [
  { value: '', label: '기본값 (미지정)' },
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
  { value: '2:3', label: '2:3' },
  { value: '3:2', label: '3:2' },
]

const IMAGE_SIZE_OPTIONS = [
  { value: '', label: '기본값 (미지정)' },
  { value: '512', label: '512' },
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
]

interface ImageForm {
  name: string
  costPerImage: string
  description: string
  facePreservation: string
  speed: string
  supportsReferenceImage: boolean
  aspectRatio: string
  imageSize: string
}

interface TextForm {
  name: string
  costPer1kInputTokens: string
  costPer1kOutputTokens: string
  maxContextTokens: string
  description: string
}

export function EditModelModal({ open, model, onClose, onSaved }: EditModelModalProps) {
  const { addToast } = useToast()
  const [imageForm, setImageForm] = useState<ImageForm | null>(null)
  const [textForm, setTextForm] = useState<TextForm | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!model) {
      setImageForm(null)
      setTextForm(null)
      return
    }
    if (model.modelType === 'image') {
      const m = model as ImageModel
      setImageForm({
        name: m.name,
        costPerImage: String(m.costPerImage),
        description: m.description,
        facePreservation: m.facePreservation,
        speed: m.speed,
        supportsReferenceImage: m.supportsReferenceImage,
        aspectRatio: m.aspectRatio ?? '',
        imageSize: m.imageSize ?? '',
      })
      setTextForm(null)
    } else {
      const m = model as TextModel
      setTextForm({
        name: m.name,
        costPer1kInputTokens: String(m.costPer1kInputTokens),
        costPer1kOutputTokens: String(m.costPer1kOutputTokens),
        maxContextTokens: String(m.maxContextTokens),
        description: m.description,
      })
      setImageForm(null)
    }
  }, [model])

  const handleSave = useCallback(async () => {
    if (!model) return
    setIsSaving(true)
    try {
      const body =
        model.modelType === 'image' && imageForm
          ? {
              name: imageForm.name,
              costPerImage: parseFloat(imageForm.costPerImage),
              description: imageForm.description,
              facePreservation: imageForm.facePreservation,
              speed: imageForm.speed,
              supportsReferenceImage: imageForm.supportsReferenceImage,
              aspectRatio: imageForm.aspectRatio || null,
              imageSize: imageForm.imageSize || null,
            }
          : textForm
            ? {
                name: textForm.name,
                costPer1kInputTokens: parseFloat(textForm.costPer1kInputTokens),
                costPer1kOutputTokens: parseFloat(textForm.costPer1kOutputTokens),
                maxContextTokens: parseInt(textForm.maxContextTokens, 10),
                description: textForm.description,
              }
            : null

      if (!body) return

      await apiFetch(`/api/admin/ai-models/${model.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
      addToast({ type: 'success', message: '모델이 수정되었습니다.' })
      onSaved()
    } catch (e: any) {
      addToast({ type: 'error', message: e.message })
    } finally {
      setIsSaving(false)
    }
  }, [model, imageForm, textForm, addToast, onSaved])

  if (!model) return null

  return (
    <Modal open={open} onClose={onClose} title={`${model.name} 수정`} size="md">
      <div className={styles.root}>
        {model.modelType === 'image' && imageForm && (
          <>
            <div className={styles.formGrid}>
              <Input
                size="sm"
                label="이름"
                value={imageForm.name}
                onChange={(e) => setImageForm((f) => f && { ...f, name: e.target.value })}
              />
              <Input
                size="sm"
                label="비용/이미지 ($)"
                type="number"
                step="0.01"
                value={imageForm.costPerImage}
                onChange={(e) => setImageForm((f) => f && { ...f, costPerImage: e.target.value })}
              />
              <Select
                size="sm"
                label="얼굴 보존"
                value={imageForm.facePreservation}
                onChange={(e) =>
                  setImageForm((f) => f && { ...f, facePreservation: e.target.value })
                }
                options={FACE_OPTIONS}
              />
              <Select
                size="sm"
                label="속도"
                value={imageForm.speed}
                onChange={(e) => setImageForm((f) => f && { ...f, speed: e.target.value })}
                options={SPEED_OPTIONS}
              />
              <Select
                size="sm"
                label="비율 (aspectRatio)"
                value={imageForm.aspectRatio}
                onChange={(e) => setImageForm((f) => f && { ...f, aspectRatio: e.target.value })}
                options={ASPECT_RATIO_OPTIONS}
              />
              <Select
                size="sm"
                label="해상도 (imageSize)"
                value={imageForm.imageSize}
                onChange={(e) => setImageForm((f) => f && { ...f, imageSize: e.target.value })}
                options={IMAGE_SIZE_OPTIONS}
              />
            </div>
            <Input
              size="sm"
              label="설명"
              value={imageForm.description}
              onChange={(e) => setImageForm((f) => f && { ...f, description: e.target.value })}
            />
            <div className={styles.checkboxField}>
              <label>
                <input
                  type="checkbox"
                  checked={imageForm.supportsReferenceImage}
                  onChange={(e) =>
                    setImageForm((f) => f && { ...f, supportsReferenceImage: e.target.checked })
                  }
                />
                참조 이미지 지원
              </label>
            </div>
          </>
        )}

        {model.modelType === 'text' && textForm && (
          <>
            <div className={styles.formGrid}>
              <Input
                size="sm"
                label="이름"
                value={textForm.name}
                onChange={(e) => setTextForm((f) => f && { ...f, name: e.target.value })}
              />
              <Input
                size="sm"
                label="Max Context Tokens"
                type="number"
                value={textForm.maxContextTokens}
                onChange={(e) =>
                  setTextForm((f) => f && { ...f, maxContextTokens: e.target.value })
                }
              />
              <Input
                size="sm"
                label="Input 비용 ($/1k tokens)"
                type="number"
                step="0.0001"
                value={textForm.costPer1kInputTokens}
                onChange={(e) =>
                  setTextForm((f) => f && { ...f, costPer1kInputTokens: e.target.value })
                }
              />
              <Input
                size="sm"
                label="Output 비용 ($/1k tokens)"
                type="number"
                step="0.0001"
                value={textForm.costPer1kOutputTokens}
                onChange={(e) =>
                  setTextForm((f) => f && { ...f, costPer1kOutputTokens: e.target.value })
                }
              />
            </div>
            <Input
              size="sm"
              label="설명"
              value={textForm.description}
              onChange={(e) => setTextForm((f) => f && { ...f, description: e.target.value })}
            />
          </>
        )}

        <div className={styles.actions}>
          <Button size="sm" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button size="sm" variant="primary" onClick={handleSave} loading={isSaving}>
            저장
          </Button>
        </div>
      </div>
    </Modal>
  )
}
